import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RunWithChoice, keyCanRun, type RunWith } from "@/components/epic-requests/run-with-choice";
import { useToast } from "@/hooks/useToast";
import { useMachines } from "@/hooks/queries/use-mailbox";
import { useAiSettings } from "@/hooks/queries/use-ai-settings";
import { useRefineEpic } from "@/hooks/queries/use-compliance";
import { ApiError } from "@/lib/api/client";
import { machineReadiness, machineServicesRemote } from "@/lib/api/mailbox";
import type { ComplianceScore } from "@/lib/api/types";

/** Mirrors the API's REFINE_NOTES_MAX_LENGTH (packages/api/src/schemas/job.ts). */
export const IMPROVE_PLAN_NOTES_MAX_LENGTH = 5000;

interface ImprovePlanDialogProps {
  epicId: string;
  /** The project's git remote — only machines that service it can run the job. */
  targetRemote?: string | null;
  /** Shown as the trigger button's label. Defaults to "Improve plan". */
  children?: React.ReactNode;
  /**
   * Controlled open state. The epic page drives this from `?improve=1` so the
   * needs-review banner (and the EPIC_CRITICAL_FAIL conversion error) can link
   * straight into the dialog. Uncontrolled when omitted.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Notes to prefill each time the dialog opens (ENG-291: the summary of an
   * Epic Request discussion). Truncated to IMPROVE_PLAN_NOTES_MAX_LENGTH so the
   * prefilled text is always sendable. The person can still edit it.
   */
  initialNotes?: string | undefined;
  /** Dialog title. Defaults to "Improve plan". */
  title?: string;
}

/** Fit notes into the planner's limit (the API rejects anything longer). */
export function truncatePlanNotes(notes: string): string {
  if (notes.length <= IMPROVE_PLAN_NOTES_MAX_LENGTH) return notes;
  const marker = "\n…(truncated)";
  return notes.slice(0, IMPROVE_PLAN_NOTES_MAX_LENGTH - marker.length) + marker;
}

/**
 * Whether the "Improve plan" action is offered for an audit score (ENG-290):
 * only when there is audit feedback to act on — critical-fail or advisory.
 * Hidden for passing and never-audited plans.
 */
export function canImprovePlan(score: ComplianceScore | null | undefined): boolean {
  return score?.tier === "critical-fail" || score?.tier === "advisory";
}

/**
 * "Improve plan" (ENG-290): sends the epic's latest audit feedback — plus any
 * notes the person adds — back to the planner as a `refine` run, on their
 * paired computer or on their API key.
 */
export function ImprovePlanDialog({
  epicId,
  targetRemote,
  children,
  open: controlledOpen,
  onOpenChange,
  initialNotes,
  title = "Improve plan",
}: ImprovePlanDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange]
  );
  const [notes, setNotes] = React.useState("");
  // Prefill once per opening (as soon as the notes are available), so a refetch
  // while the dialog is open never clobbers the person's edits.
  const prefilled = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      prefilled.current = false;
      return;
    }
    if (!prefilled.current && initialNotes !== undefined) {
      prefilled.current = true;
      setNotes(truncatePlanNotes(initialNotes));
    }
  }, [open, initialNotes]);
  const [selectedMachineId, setSelectedMachineId] = React.useState<string | null>(null);
  const [runWithChoice, setRunWithChoice] = React.useState<RunWith | null>(null);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: machines } = useMachines();
  const { data: aiSettings } = useAiSettings();
  const refine = useRefineEpic();

  const hasComputer = (machines?.length ?? 0) > 0;
  const keyReady = keyCanRun(aiSettings, "execution", hasComputer);
  const runWith: RunWith = runWithChoice ?? (!hasComputer && keyReady ? "key" : "computer");

  // Machines that are launch-ready and service this project's repository.
  const eligible = React.useMemo(
    () =>
      (machines ?? []).filter(
        (m) => machineReadiness(m) === "ready" && machineServicesRemote(m, targetRemote ?? null)
      ),
    [machines, targetRemote]
  );

  React.useEffect(() => {
    if (!open) return;
    setSelectedMachineId((current) =>
      current && eligible.some((m) => m.id === current) ? current : (eligible[0]?.id ?? null)
    );
  }, [open, eligible]);

  // On a key with no paired computer, the run goes to a CompBox cloud workspace.
  const toCloud = runWith === "key" && !hasComputer;
  const notesTooLong = notes.length > IMPROVE_PLAN_NOTES_MAX_LENGTH;
  const canSend =
    !refine.isPending && !notesTooLong && (toCloud ? keyReady : selectedMachineId !== null);

  const handleSend = async () => {
    if (!canSend) return;
    setSendError(null);
    try {
      const job = await refine.mutateAsync({
        epicId,
        notes,
        ...(!toCloud && selectedMachineId ? { machineId: selectedMachineId, useAiKey: runWith === "key" } : {}),
      });
      setOpen(false);
      setNotes("");
      toast("Sent to the planner to improve the plan");
      void navigate(`/mailbox/jobs/${job.id}`);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Failed to send the plan back to the planner";
      setSendError(message);
      toast(message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSendError(null);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          {children ?? (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Improve plan
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            The planner gets the latest audit feedback for this epic and revises the plan to fix it.
            Add anything else it should know.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="improve-plan-notes">Notes (optional)</Label>
            <Textarea
              id="improve-plan-notes"
              value={notes}
              onChange={(e) => { setNotes(e.target.value); }}
              placeholder="e.g. Split the API work into its own feature"
              rows={4}
              maxLength={IMPROVE_PLAN_NOTES_MAX_LENGTH}
            />
            <p className="text-xs text-muted-foreground text-right">
              {notes.length}/{IMPROVE_PLAN_NOTES_MAX_LENGTH}
            </p>
            {notesTooLong && (
              <p className="text-xs text-destructive">
                Notes must be at most {IMPROVE_PLAN_NOTES_MAX_LENGTH} characters.
              </p>
            )}
          </div>

          <RunWithChoice
            settings={aiSettings}
            kind="execution"
            hasComputer={hasComputer}
            value={runWith}
            onChange={setRunWithChoice}
          />

          {!toCloud && (
            eligible.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {hasComputer
                  ? "None of your computers is ready to work on this project right now."
                  : "Pair a computer to run the planner, or use your API key."}
              </p>
            ) : eligible.length > 1 ? (
              <div className="space-y-1.5">
                <Label>Computer</Label>
                <Select value={selectedMachineId ?? ""} onValueChange={setSelectedMachineId}>
                  <SelectTrigger aria-label="Computer">
                    <SelectValue placeholder="Choose a computer" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligible.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null
          )}

          {sendError && (
            <p role="alert" className="text-sm text-destructive">
              {sendError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); }}>
            Cancel
          </Button>
          <Button onClick={() => { void handleSend(); }} disabled={!canSend}>
            {refine.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send to planner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
